import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import api, { UserTier, TierLimits, TIER_LIMITS } from '../services/api';
import { track, identify } from '../services/analytics';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Bootstrap the Authorization header at module-load time. Without this, auth-gated requests
// fired from a child component's mount effect (e.g. AdminDashboard.loadData) race ahead of
// AuthProvider's own useEffect that attaches the header, producing a spurious 401 on first load.
const bootstrapToken = localStorage.getItem('token');
if (bootstrapToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${bootstrapToken}`;
}

// Tier limits are owned by the backend and returned by GET /api/auth/me. We cache the last
// response together with the tier it belongs to, so a cached value is never shown for a
// different tier (e.g. right after starting impersonation) — until /me answers for the current
// tier we fall back to the bundled TIER_LIMITS defaults.
type CachedLimits = { tier: UserTier; limits: TierLimits };

const readCachedLimits = (): CachedLimits | null => {
  try {
    const stored = localStorage.getItem('tierLimits');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed?.tier && typeof parsed?.limits?.maxCaseStudies === 'number' && typeof parsed?.limits?.maxPhotos === 'number') {
      return parsed as CachedLimits;
    }
    return null;
  } catch {
    return null;
  }
};

interface ImpersonatedUser {
  userId: string;
  username: string;
  tier: UserTier;
}

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  userId: string | null;
  tier: UserTier;
  tierLimits: TierLimits;
  isAdmin: boolean;
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  login: (identifier: string, password: string) => Promise<void>;
  loginWithToken: (token: string, userId: string) => void;
  logout: () => void;
  register: (email: string, password: string, captchaToken: string, source?: string) => Promise<void>;
  refreshUserInfo: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [userId, setUserId] = useState<string | null>(localStorage.getItem('userId'));
  const [tier, setTier] = useState<UserTier>((localStorage.getItem('tier') as UserTier) || 'free');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [isAdmin, setIsAdmin] = useState<boolean>(localStorage.getItem('isAdmin') === 'true');
  const [isImpersonating, setIsImpersonating] = useState<boolean>(localStorage.getItem('isImpersonating') === 'true');
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(() => {
    const stored = localStorage.getItem('impersonatedUser');
    return stored ? JSON.parse(stored) : null;
  });

  const [cachedLimits, setCachedLimits] = useState<CachedLimits | null>(readCachedLimits);

  const tierLimits: TierLimits =
    cachedLimits && cachedLimits.tier === tier ? cachedLimits.limits : TIER_LIMITS[tier];

  const refreshUserInfo = async () => {
    try {
      const userInfo = await api.getUserInfo();
      setTier(userInfo.tier);
      setIsAdmin(userInfo.isAdmin || false);
      localStorage.setItem('tier', userInfo.tier);
      localStorage.setItem('isAdmin', String(userInfo.isAdmin || false));

      if (userInfo.limits) {
        const fresh: CachedLimits = { tier: userInfo.tier, limits: userInfo.limits };
        setCachedLimits(fresh);
        localStorage.setItem('tierLimits', JSON.stringify(fresh));
      }

      // Update impersonation state from server
      if (userInfo.isImpersonating) {
        setIsImpersonating(true);
        localStorage.setItem('isImpersonating', 'true');
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch user info to get tier
      refreshUserInfo();
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const login = async (identifier: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        identifier,
        password
      });
      const { token, userId } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      setToken(token);
      setUserId(userId);
      setIsAuthenticated(true);
      // Clear any impersonation state on fresh login
      setIsImpersonating(false);
      setImpersonatedUser(null);
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('impersonatedUser');
    } catch (error) {
      throw error;
    }
  };

  const loginWithToken = (newToken: string, newUserId: string) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('userId', newUserId);
    setToken(newToken);
    setUserId(newUserId);
    setIsAuthenticated(true);
    setIsImpersonating(false);
    setImpersonatedUser(null);
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('impersonatedUser');
  };

  const register = async (email: string, password: string, captchaToken: string, source?: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        email,
        password,
        captchaToken,
        ...(source ? { source } : {}),
      });
      const { token, userId } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      setToken(token);
      setUserId(userId);
      setIsAuthenticated(true);
      identify(userId, { signupSource: source || null });
      track('signup_complete', { source: source || null, tier: 'free' });
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('tier');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('impersonatedUser');
    localStorage.removeItem('tierLimits');
    setToken(null);
    setUserId(null);
    setTier('free');
    setCachedLimits(null);
    setIsAdmin(false);
    setIsAuthenticated(false);
    setIsImpersonating(false);
    setImpersonatedUser(null);
  };

  const impersonate = async (targetUserId: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/impersonate/${targetUserId}`);
      const { token: newToken, impersonatedUser: targetUser } = response.data;

      // Store the impersonation token
      localStorage.setItem('token', newToken);
      localStorage.setItem('userId', targetUser.userId);
      localStorage.setItem('tier', targetUser.tier);
      localStorage.setItem('isImpersonating', 'true');
      localStorage.setItem('impersonatedUser', JSON.stringify(targetUser));

      setToken(newToken);
      setUserId(targetUser.userId);
      setTier(targetUser.tier);
      setIsImpersonating(true);
      setImpersonatedUser(targetUser);

      // Update axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    } catch (error) {
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/stop-impersonation`);
      const { token: adminToken, userId: adminUserId, username, isAdmin: adminIsAdmin } = response.data;

      // Restore admin session
      localStorage.setItem('token', adminToken);
      localStorage.setItem('userId', adminUserId);
      localStorage.setItem('isAdmin', String(adminIsAdmin));
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('impersonatedUser');

      setToken(adminToken);
      setUserId(adminUserId);
      setIsAdmin(adminIsAdmin);
      setIsImpersonating(false);
      setImpersonatedUser(null);

      // Update axios header and refresh user info
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      await refreshUserInfo();
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      token,
      userId,
      tier,
      tierLimits,
      isAdmin,
      isImpersonating,
      impersonatedUser,
      login,
      loginWithToken,
      logout,
      register,
      refreshUserInfo,
      impersonate,
      stopImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
};
